import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowRight, Home, Radio } from "lucide-react";
import { Logo } from "../components/Logo";
import { slotWindowState, formatSlotIst } from "../lib/calendar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TrainingsJoin() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    (async () => {
      try {
        // Look up all bookings admin-side isn't public. We just resolve session from
        // any booking with this id — the resume flow needs a public GET.
        // Simplest: hit /api/trainings and match manually by fetching one via a public
        // resolve endpoint. We use /api/trainings to fetch all sessions, and rely on
        // localStorage or the pathname alone. Since we don't have GET /booking/{id}
        // as public, the pragmatic flow: reopen the session catalog with the booking id,
        // and prompt user to pick lang/device again if we can't resolve.
        const { data: sessions } = await axios.get(`${API}/trainings`);
        // We store recent bookings in localStorage after confirmation, use that if available
        const local = JSON.parse(localStorage.getItem("cpn_bookings") || "{}");
        const b = local[bookingId];
        if (b) {
          const s = sessions.find((x) => x.id === b.session_id);
          if (s) {
            setState({ loading: false, session: s, booking: b });
            return;
          }
        }
        // Fallback: show a generic "open catalog" screen
        setState({ loading: false, fallback: true });
      } catch (e) {
        setState({ loading: false, error: "Could not load session." });
      }
    })();
  }, [bookingId]);

  if (state.loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF5E1A]" />
      </div>
    );
  }

  if (state.fallback || state.error) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center px-5">
        <div className="glass-strong rounded-3xl p-6 md:p-8 max-w-md w-full text-center">
          <Logo className="h-8 mx-auto mb-4" />
          <h1 className="font-heading text-xl font-bold text-[#0F172A]">
            Session open karne ke liye trainings page pe wapas jao
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Aapki booking calendar mein safe hai. Sessions catalog kholo aur
            live session ko join karo.
          </p>
          <button
            type="button"
            onClick={() => navigate("/trainings")}
            className="mt-5 w-full rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white py-3.5 text-base font-semibold inline-flex items-center justify-center gap-2"
          >
            <Home className="w-4 h-4" /> Trainings catalog
          </button>
        </div>
      </div>
    );
  }

  const { session, booking } = state;
  const w = slotWindowState(booking.slot, session.duration_minutes);
  const canJoin = w.state === "live" || w.state === "opening";

  return (
    <div className="min-h-[100dvh] flex items-center justify-center px-5">
      <div className="glass-strong rounded-3xl p-6 md:p-8 max-w-md w-full">
        <Logo className="h-7 mb-4" />
        <h1 className="font-heading text-xl font-bold text-[#0F172A]">
          {session.title}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Slot: {formatSlotIst(booking.slot)}
        </p>
        {canJoin ? (
          <button
            type="button"
            onClick={() => navigate("/trainings")}
            className="mt-5 w-full rounded-2xl bg-[#FF5E1A] hover:bg-[#E04F00] text-white py-3.5 text-base font-semibold inline-flex items-center justify-center gap-2"
          >
            <Radio className="w-4 h-4" /> Join now
            <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <div className="mt-5 space-y-3">
            <p className="text-sm text-slate-600">
              {w.state === "future"
                ? "Aapka session abhi shuru nahi hua. 10 minutes pehle join button active hoga."
                : "Session khatam ho gaya. Koi baat nahi — next slot check karo ya aur session dekho."}
            </p>
            <button
              type="button"
              onClick={() => navigate("/trainings")}
              className="w-full rounded-2xl bg-[#0F172A] text-white py-3 text-sm font-semibold inline-flex items-center justify-center gap-2"
            >
              Explore all sessions <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
